/**
 * Fechas y calendarios en el idioma de la pantalla, sin cargar locales de moment ni de date-fns.
 *
 * Los selectores de período y de tramos del clásico escriben las fechas a mano («2026-10-31») o con
 * moment sin locale («01 Oct 2026»), y el `DayPicker` sale con su locale por defecto («October 2026»,
 * «Su Mo Tu»). Estos helpers resuelven lo mismo con `Intl`, que ya viene con el navegador, y los
 * componentes los usan SÓLO cuando quien los monta les pasa un idioma (`dateLocale`): el clásico
 * queda como estaba. Primer uso: el wizard «Nueva línea de control» (QA-VIS-wizard-04 y -06).
 */

function capitalize(text) {
    return text ? text.charAt(0).toUpperCase() + text.slice(1) : text;
}

/** «01 oct 2026» / «Oct 01, 2026». */
export function formatShortDate(date, language) {
    if (!date) return '';
    const value = date instanceof Date ? date : new Date(date);
    if (Number.isNaN(value.getTime())) return '';
    return new Intl.DateTimeFormat(language, {day: '2-digit', month: 'short', year: 'numeric'})
        .format(value)
        .replace(/\./g, '');
}

/**
 * En qué mes abre el calendario de una fecha que tiene que caer dentro de un período: el de la fecha
 * ya elegida; si no hay, el de hoy cuando hoy está dentro del período; y si no, el del inicio.
 * `DayPicker` abre por defecto en el mes actual, y con un período que empieza el mes que viene el
 * usuario veía un mes entero deshabilitado (QA en producción, 27/09). `undefined` = el default.
 */
export function initialMonthWithin(selected, from, to) {
    if (selected) return new Date(selected);
    if (!from) return undefined;
    const today = new Date();
    const start = new Date(from);
    const end = to ? new Date(to) : null;
    if (today >= start && (!end || today <= end)) return undefined;
    return start;
}

/**
 * Las props de un `DayPicker` (react-day-picker v9) para mostrarlo en `language`: el título del mes
 * («Octubre de 2026»), los días de la semana en dos letras («Lu Ma Mi») y la semana empezando el
 * lunes en español.
 */
export function dayPickerLocaleProps(language) {
    const caption = new Intl.DateTimeFormat(language, {month: 'long', year: 'numeric'});
    const weekday = new Intl.DateTimeFormat(language, {weekday: 'short'});
    return {
        formatters: {
            formatCaption: month => capitalize(caption.format(month)),
            formatWeekdayName: day => capitalize(weekday.format(day).replace(/\./g, '').slice(0, 2)),
        },
        weekStartsOn: String(language || '').toLowerCase().startsWith('es') ? 1 : 0,
    };
}
